import { useCallback, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useSettingsStore } from '../lib/stores/settingsStore';
import {
  effectiveRequiredFieldsForCountry,
  validateClientForContract,
  type RequiredClientField,
} from '../lib/utils/validateClientForContract';
import { countryFromVatNumber } from '../lib/utils/countries';
import { ClientRequiredFieldsDrawer } from '../components/deals/ClientRequiredFieldsDrawer';

const CLIENT_SELECT =
  'business_name, vat_number, tax_code, sdi_code, pec, email, email_amministrazione, street_address, street_number, postal_code, city, province, country, client_contacts!client_contacts_client_id_fkey(area, email)';

interface GateState {
  clientId: string;
  client: Record<string, any> | null;
  missing: RequiredClientField[];
  /** Campi richiesti al netto degli esoneri per paese (estero: no SDI/PEC/CF). */
  effectiveRequired: string[];
  onComplete: () => void;
  dealId?: string;
  dealGroupNumbers?: string[];
  dealGroupName?: string;
  clientName?: string;
}

/** Contesto deal opzionale per il token `whatsapp_group`. */
export interface DealGateContext {
  dealId: string;
  clientName?: string;
}

/**
 * Gate dati cliente per contratto/firma. `guard(clientId, action, dealCtx?)`:
 *  - apre SEMPRE un modale (Notion-like) con i campi obbligatori configurati
 *    (settings.required_client_fields_for_contract) precompilati: l'utente
 *    verifica/corregge e conferma; al salvataggio esegue action()
 *  - se nessun campo è flaggato nelle impostazioni → esegue subito action()
 *
 * Quando il token `whatsapp_group` è abilitato e `dealCtx.dealId` è fornito,
 * recupera `whatsapp_group_numbers`/`whatsapp_group_name` dal deal e li inietta
 * nel record di validazione (i dati vivono sul deal, non sul cliente).
 *
 * Ritorna anche `modal` da renderizzare nel componente.
 */
export function useContractClientGate() {
  const { settings } = useSettingsStore();
  const [state, setState] = useState<GateState | null>(null);

  const guard = useCallback(
    async (
      clientId: string | null | undefined,
      onComplete: () => void,
      dealCtx?: DealGateContext,
    ) => {
      const required = (settings as any)?.required_client_fields_for_contract as string[] | undefined;
      if (!clientId || !required || required.length === 0) {
        onComplete();
        return;
      }

      const { data: client } = await supabase
        .from('clients')
        .select(CLIENT_SELECT)
        .eq('id', clientId)
        .maybeSingle();

      // Modello Contatti/Aziende first-class: il contatto amministrazione può essere
      // un referente in client_relationships (client_contacts non più scritta). Senza
      // questo merge il fallback email_amministrazione fallisce e il gate blocca a torto.
      if (client) {
        const { fetchReferentiAsContacts, mergeContactsByEmail } = await import('../lib/utils/fetchReferentiContacts');
        const refMap = await fetchReferentiAsContacts([clientId]);
        (client as any).client_contacts = mergeContactsByEmail(
          ((client as any).client_contacts ?? []) as any,
          refMap[clientId] ?? [],
        );
      }

      // Se il token whatsapp_group è richiesto e abbiamo il dealId, leggiamo i
      // campi dal deal e li iniettiamo nel record di validazione.
      let dealGroupNumbers: string[] | undefined;
      let dealGroupName: string | undefined;
      let record: Record<string, any> | null = client;

      const needsWhatsappGroup = required.includes('whatsapp_group') && !!dealCtx?.dealId;
      if (needsWhatsappGroup) {
        const { data: dealData } = await supabase
          .from('deals')
          .select('whatsapp_group_numbers, whatsapp_group_name, whatsapp_reuse_group_jid')
          .eq('id', dealCtx!.dealId)
          .maybeSingle();
        dealGroupNumbers = (dealData?.whatsapp_group_numbers as string[] | null) ?? undefined;
        dealGroupName = (dealData?.whatsapp_group_name as string | null) ?? undefined;
        record = client
          ? {
              ...client,
              whatsapp_group_numbers: dealGroupNumbers ?? [],
              whatsapp_group_name: dealGroupName ?? '',
              // Riuso gruppo esistente: il validatore bypassa numeri+nome se presente.
              whatsapp_reuse_group_jid: (dealData as any)?.whatsapp_reuse_group_jid ?? null,
            }
          : null;
      }

      // Clienti esteri riconosciuti: SDI, PEC e CF esonerati automaticamente.
      // Se il campo country è vuoto, il paese si deriva dal prefisso P.IVA
      // (es. "FR799..." → Francia): senza fallback un cliente estero senza
      // country ricadrebbe a torto nelle regole italiane.
      const clientCountry: string | null =
        (client as any)?.country ||
        countryFromVatNumber((client as any)?.vat_number)?.name ||
        null;
      const effectiveRequired = effectiveRequiredFieldsForCountry(
        required,
        clientCountry,
      );
      const validation = validateClientForContract(record, effectiveRequired);

      // Il modale si apre SEMPRE, anche a dati completi: l'utente verifica e
      // conferma i campi flaggati prima di firma/invio/upload contratto.
      // `missing` serve solo a evidenziare gli eventuali campi vuoti.
      setState({
        clientId,
        client: client ? { ...client, country: clientCountry } : client,
        missing: validation.missingFields,
        effectiveRequired,
        onComplete,
        dealId: dealCtx?.dealId,
        dealGroupNumbers,
        dealGroupName,
        clientName: dealCtx?.clientName,
      });
    },
    [settings],
  );

  const modal = state ? (
    <ClientRequiredFieldsDrawer
      isOpen
      clientId={state.clientId}
      client={state.client}
      requiredFields={state.effectiveRequired as RequiredClientField[]}
      clientCountry={(state.client as any)?.country ?? null}
      onClose={() => setState(null)}
      onSaved={() => {
        const cb = state.onComplete;
        setState(null);
        cb();
      }}
      dealId={state.dealId}
      dealGroupNumbers={state.dealGroupNumbers}
      dealGroupName={state.dealGroupName}
      clientName={state.clientName}
    />
  ) : null;

  return { guard, modal };
}
